import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminPage, AdminPageHeader } from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DmReport, DmSuspendMode, artistHubEnabled, dmChannelSuspend, dmModerate, dmModerationList } from "@/services/firebase/artistHub";
import { getActor } from "@/services/firebase/actor";
import { DM_DECISION_LABELS, DmModerationDecision, decisionsFor, suspendConfirmed } from "@/lib/dm";
import DmRefundClaimsPanel from "./DmRefundClaimsPage";

const fmt = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString("zh-TW", { hour12: false }) : "—");
const errorText = (e: unknown) => (e instanceof Error ? e.message : "操作未完成，請重試");
const sourceLabels: Record<string, string> = { fan: "粉絲檢舉", filter: "關鍵字", artist: "藝人團隊" };
const targetLabels: Record<string, string> = { reply: "粉絲回覆", message: "藝人／小編訊息", broadcast: "廣播" };
const modeLabels: Record<DmSuspendMode, string> = { suspend: "停用（可讀不可寫）", close: "關閉（可讀不可寫）", restore: "恢復" };

/**
 * C-02: 鐵粉私訊審核（contract v2.2 §1d 營運 action）. Staff only; not behind the RC gate.
 *   dmModerationList → queue of fan reports, keyword-filter hits and artist reports
 *   dmModerate {decision: hide | dismiss | block_fan (reply only) | retract_broadcast (broadcast only)}
 *   dmChannelSuspend {mode: suspend | close | restore} — confirmation requires typing the artist's name.
 * Stopping the sale is separate (藝人頁 → 鐵粉月卡／商店商品).
 */
export default function DmModerationPage() {
  const client = useQueryClient();
  const [status, setStatus] = useState<"open" | "resolved">("open");
  const [artistFilter, setArtistFilter] = useState("");
  const [appliedArtist, setAppliedArtist] = useState("");
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const cursor = pages[pages.length - 1];
  const list = useQuery({
    queryKey: ["dm-moderation", status, appliedArtist, cursor],
    queryFn: () => dmModerationList({ status, artistId: appliedArtist || null, cursor }),
    enabled: artistHubEnabled,
    retry: false,
  });
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState("");
  const moderate = useMutation({
    mutationFn: ({ report, decision }: { report: DmReport; decision: DmModerationDecision }) =>
      dmModerate(report.reportId, decision, notes[report.reportId]?.trim() || undefined),
    onSuccess: (r) => {
      setNotice(`已處理檢舉 ${r.reportId}：${r.resolution ? DM_DECISION_LABELS[r.resolution] : "完成"}`);
      void client.invalidateQueries({ queryKey: ["dm-moderation"] });
    },
  });
  const [suspendArtist, setSuspendArtist] = useState("");
  const [suspendMode, setSuspendMode] = useState<DmSuspendMode>("suspend");
  const [suspendReason, setSuspendReason] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!artistHubEnabled) {
    return (
      <AdminPage>
        <AdminPageHeader title="私訊審核" />
        <p>此環境尚未開放藝人專區（鐵粉私訊）。</p>
      </AdminPage>
    );
  }
  const items = list.data?.items ?? [];
  return (
    <AdminPage maxWidth="wide">
      <AdminPageHeader
        title="私訊審核"
        subtitle="鐵粉私訊的檢舉與關鍵字命中；處置會寫入稽核紀錄"
        loading={list.isFetching}
        onRefresh={() => void list.refetch()}
      />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>檢舉佇列</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex gap-2" role="group" aria-label="狀態">
              {(["open", "resolved"] as const).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={status === s ? "default" : "outline"}
                  aria-pressed={status === s}
                  onClick={() => {
                    setStatus(s);
                    setPages([null]);
                  }}
                >
                  {s === "open" ? "待處理" : "已處理"}
                </Button>
              ))}
            </div>
            <div>
              <Label htmlFor="dm-mod-artist">藝人 ID（選填）</Label>
              <Input id="dm-mod-artist" value={artistFilter} onChange={(e) => setArtistFilter(e.target.value)} placeholder="全部藝人" />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setAppliedArtist(artistFilter.trim());
                setPages([null]);
              }}
            >
              套用
            </Button>
          </div>
          {(list.isError || moderate.isError) && (
            <p role="alert" className="text-destructive">
              {errorText(moderate.error ?? list.error)}
            </p>
          )}
          {notice && <p role="status">{notice}</p>}
          {list.isPending && <p>讀取檢舉…</p>}
          {list.data && items.length === 0 && <p className="text-sm text-muted-foreground">{status === "open" ? "目前沒有待處理的檢舉。" : "沒有已處理的檢舉。"}</p>}
          {items.length > 0 && (
            <ul className="divide-y">
              {items.map((r) => (
                <li key={r.reportId} className="space-y-2 py-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge variant={r.source === "filter" ? "warning" : r.source === "artist" ? "info" : "danger"}>{sourceLabels[r.source ?? ""] ?? r.source}</Badge>
                    <Badge variant="outline">{targetLabels[r.target ?? ""] ?? r.target}</Badge>
                    <span className="text-muted-foreground">藝人 {r.artistId}</span>
                    <span className="text-muted-foreground">粉絲 {r.uid ?? "—"}</span>
                    <span className="text-xs text-muted-foreground">{fmt(r.createdAt)}</span>
                    {r.status === "resolved" && (
                      <Badge variant="success">
                        {r.resolution ? DM_DECISION_LABELS[r.resolution] : "已處理"} · {fmt(r.resolvedAt)}
                      </Badge>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap break-words rounded bg-muted p-2">{r.snippet || "（無內容摘要）"}</p>
                  {r.reason && <p className="text-sm">檢舉原因：{r.reason}</p>}
                  {r.status === "open" && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        className="w-64"
                        aria-label="處理備註"
                        maxLength={200}
                        placeholder="處理備註（選填，≤200 字）"
                        value={notes[r.reportId] ?? ""}
                        onChange={(e) => setNotes((n) => ({ ...n, [r.reportId]: e.target.value }))}
                      />
                      {decisionsFor(r.target).map((decision) => (
                        <Button
                          key={decision}
                          size="sm"
                          variant={decision === "dismiss" ? "outline" : "destructive"}
                          disabled={moderate.isPending}
                          onClick={() => {
                            if (decision === "dismiss" || window.confirm(`確定「${DM_DECISION_LABELS[decision]}」？`)) moderate.mutate({ report: r, decision });
                          }}
                        >
                          {DM_DECISION_LABELS[decision]}
                        </Button>
                      ))}
                      {r.artistId && (
                        <Button size="sm" variant="ghost" onClick={() => setSuspendArtist(r.artistId ?? "")}>
                          帶入停用聊天室
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            {pages.length > 1 && (
              <Button size="sm" variant="outline" disabled={list.isFetching} onClick={() => setPages([null])}>
                回到最新
              </Button>
            )}
            {list.data?.nextCursor && (
              <Button size="sm" variant="outline" disabled={list.isFetching} onClick={() => setPages((p) => [...p, list.data?.nextCursor ?? null])}>
                下一頁
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>停用藝人聊天室</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            停用／關閉後聊天室只能讀不能寫（粉絲回覆、投票、藝人發送、私人回覆、愛心都會被擋）。這裡不會停售：停售請到藝人頁的鐵粉月卡／商店商品設定。
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label htmlFor="dm-suspend-artist">藝人 ID</Label>
              <Input id="dm-suspend-artist" value={suspendArtist} onChange={(e) => setSuspendArtist(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="dm-suspend-mode">動作</Label>
              <select
                id="dm-suspend-mode"
                className="block rounded border bg-background px-2 py-2 text-sm"
                value={suspendMode}
                onChange={(e) => setSuspendMode(e.target.value as DmSuspendMode)}
              >
                {(Object.keys(modeLabels) as DmSuspendMode[]).map((m) => (
                  <option key={m} value={m}>
                    {modeLabels[m]}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[16rem] flex-1">
              <Label htmlFor="dm-suspend-reason">原因（≤200 字）</Label>
              <Input id="dm-suspend-reason" maxLength={200} value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} />
            </div>
            <Button variant={suspendMode === "restore" ? "default" : "destructive"} disabled={!suspendArtist.trim()} onClick={() => setDialogOpen(true)}>
              {suspendMode === "restore" ? "恢復聊天室…" : "停用聊天室…"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <DmRefundClaimsPanel />

      {dialogOpen && (
        <SuspendDialog
          artistId={suspendArtist.trim()}
          mode={suspendMode}
          reason={suspendReason.trim()}
          onClose={() => setDialogOpen(false)}
          onDone={(msg) => {
            setDialogOpen(false);
            setNotice(msg);
            setSuspendReason("");
          }}
        />
      )}
    </AdminPage>
  );
}

function SuspendDialog({
  artistId,
  mode,
  reason,
  onClose,
  onDone,
}: {
  artistId: string;
  mode: DmSuspendMode;
  reason: string;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [typed, setTyped] = useState("");
  const actor = useQuery({ queryKey: ["actor", artistId], queryFn: () => getActor(artistId), retry: false });
  const name = actor.data?.name ?? "";
  const submit = useMutation({
    mutationFn: () => dmChannelSuspend(artistId, mode, reason || undefined),
    onSuccess: (r) => onDone(`「${name}」聊天室已${r.status === "active" ? "恢復" : r.status === "closed" ? "關閉" : "停用"}`),
  });
  const ok = suspendConfirmed(typed, name);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === "restore" ? "恢復聊天室" : `${modeLabels[mode]}`}</DialogTitle>
          <DialogDescription>
            {mode === "restore"
              ? "恢復後粉絲與藝人可以再次回覆與發送。"
              : "所有鐵粉與藝人團隊將只能閱讀、無法寫入；既有訂閱不會自動停售或退款。"}
          </DialogDescription>
        </DialogHeader>
        {actor.isPending && <p>讀取藝人資料…</p>}
        {actor.data === null && <p className="text-destructive">找不到藝人 {artistId}，請確認 ID。</p>}
        {actor.isError && <p className="text-destructive">{errorText(actor.error)}</p>}
        {name && (
          <div className="space-y-2">
            <Label htmlFor="dm-suspend-confirm">
              請輸入藝人名稱「<strong>{name}</strong>」以確認
            </Label>
            <Input id="dm-suspend-confirm" value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />
            {reason && <p className="text-sm text-muted-foreground">原因：{reason}</p>}
          </div>
        )}
        {submit.isError && (
          <p role="alert" className="text-destructive">
            {errorText(submit.error)}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            取消
          </Button>
          <Button variant={mode === "restore" ? "default" : "destructive"} disabled={!ok || submit.isPending} onClick={() => submit.mutate()}>
            確認{mode === "restore" ? "恢復" : "停用"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
